// ─── Orio360 Design Tokens ─────────────────────────────────────────────────────
// Three Core Principles: No Borders, Mono Data, Glass Depth
//
// Canonical source. Both apps/pos and apps/admin should import from here.

/** Glass Depth: translucent background + backdrop blur + soft shadow. No borders. */
export const GLASS =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

/** Glass panel with hover lift for interactive cards */
export const GLASS_INTERACTIVE =
  `${GLASS} transition-all duration-200 hover:shadow-lg cursor-pointer`;

/** Glass panel for form sections — slightly lighter */
export const GLASS_FORM =
  'rounded-2xl bg-white/70 dark:bg-neutral-900/70 backdrop-blur-sm shadow-[0_8px_24px_-14px_rgba(15,23,42,0.18)] dark:shadow-[0_8px_24px_-14px_rgba(0,0,0,0.4)]';

/** Filter bar glass panel.
 *
 * `relative z-20` ensures any open Select / Dropdown inside the filter bar
 * paints above the sibling DataTable's sticky `<thead>` (which is `z-10`).
 * Without this, the filter bar's `backdrop-blur-sm` establishes its own
 * stacking context at `z-auto`, trapping the dropdown's `z-50` inside that
 * context — and the later-painted sticky header wins (regression #107:
 * Products page filter dropdowns clipped behind the table header).
 */
export const GLASS_FILTER =
  'relative z-20 rounded-xl bg-white/60 dark:bg-neutral-900/60 backdrop-blur-sm shadow-[0_4px_16px_-8px_rgba(15,23,42,0.12)] dark:shadow-[0_4px_16px_-8px_rgba(0,0,0,0.3)]';

/** Utility: join classes, filtering falsy values */
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Tone palettes (KPI cards, stat cards) ──────────────────────────────────

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'teal' | 'violet' | 'brand';

export function toneClasses(tone: Tone) {
  switch (tone) {
    case 'success':
      return {
        icon: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
        accent: 'from-emerald-500/20 to-emerald-500/0',
      };
    case 'warning':
      return {
        icon: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
        accent: 'from-amber-500/18 to-amber-500/0',
      };
    case 'danger':
      return {
        icon: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
        badge: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
        accent: 'from-rose-500/18 to-rose-500/0',
      };
    case 'info':
      return {
        icon: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        badge: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
        accent: 'from-sky-500/18 to-sky-500/0',
      };
    case 'teal':
      return {
        icon: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
        badge: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
        accent: 'from-teal-500/18 to-teal-500/0',
      };
    case 'violet':
      return {
        icon: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
        badge: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
        accent: 'from-violet-500/18 to-violet-500/0',
      };
    case 'brand':
      return {
        icon: 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400',
        badge: 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300',
        accent: 'from-brand-500/18 to-brand-500/0',
      };
    default:
      return {
        icon: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        badge: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
        accent: 'from-indigo-500/18 to-indigo-500/0',
      };
  }
}
